import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Button, Chip, TextField, InputAdornment, Pagination,
  IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import { fetchData, putData } from "../../BaseUrl";
import Toast from "../../DynamicComponent/Toast";
import { exportToCSV, filterRows } from "../../utils/tableUtils";

const STATUS_CONFIG = {
  pending:  { label: "Pending",  color: "#f59e0b", bg: "#fffbeb" },
  approved: { label: "Approved", color: "#16a34a", bg: "#f0fdf4" },
  rejected: { label: "Rejected", color: "#dc2626", bg: "#fef2f2" },
};

const FALLBACK_LEAVES = [
  { id: "1", teacherName: "Mrs. Kavitha Rajan", leaveFrom: "2025-02-10", leaveTo: "2025-02-12", totalDays: 3, leaveType: "Medical", reason: "Doctor appointment", status: "pending" },
  { id: "2", teacherName: "Mr. Suresh Kumar", leaveFrom: "2025-02-15", leaveTo: "2025-02-15", totalDays: 1, leaveType: "Personal", reason: "Family function", status: "approved" },
  { id: "3", teacherName: "Mrs. Priya Mani", leaveFrom: "2025-03-01", leaveTo: "2025-03-03", totalDays: 3, leaveType: "Medical", reason: "Surgery", status: "pending" },
];

const ROWS_PER_PAGE = 10;

const VPLeavePortal = () => {
  const [rows, setRows] = useState([]);
  const [filterText, setFilterText] = useState("");
  const [showFilter, setShowFilter] = useState(false);
  const [page, setPage] = useState(1);
  const [actionDialog, setActionDialog] = useState({ open: false, row: null, action: "approved", note: "" });
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });

  const load = useCallback(async () => {
    try {
      const res = await fetchData("teacher/leave/list-all");
      if (res?.data) {
        const data = Array.isArray(res.data) ? res.data : res.data.data || [];
        setRows(data.length > 0 ? data : FALLBACK_LEAVES);
      } else setRows(FALLBACK_LEAVES);
    } catch (_) { setRows(FALLBACK_LEAVES); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = filterRows(rows, filterText);
  const pageCount = Math.ceil(filtered.length / ROWS_PER_PAGE);
  const pageRows = filtered.slice((page - 1) * ROWS_PER_PAGE, page * ROWS_PER_PAGE);

  const handleApproval = async () => {
    const { row, action, note } = actionDialog;
    try {
      await putData(`teacher/leave/update-status/${row.id}`, { status: action, note });
      setToast({ open: true, severity: "success", message: `Leave ${action} successfully!` });
      setActionDialog({ open: false, row: null, action: "approved", note: "" });
      load();
    } catch (err) {
      setToast({ open: true, severity: "error", message: err?.response?.data?.message || "Failed to update." });
    }
  };

  const handleExport = () => {
    const cols = [
      { label: "Teacher", field: "teacherName" },
      { label: "From", field: "leaveFrom" },
      { label: "To", field: "leaveTo" },
      { label: "Days", field: "totalDays" },
      { label: "Type", field: "leaveType" },
      { label: "Status", field: "status" },
    ];
    exportToCSV(cols, filtered.map(r => ({
      teacherName: r.teacherName,
      leaveFrom: r.leaveFrom,
      leaveTo: r.leaveTo,
      totalDays: r.totalDays,
      leaveType: r.leaveType,
      status: r.status,
    })), "teacher_leaves.csv");
  };

  const StatusChip = ({ status }) => {
    const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
    return <Chip label={cfg.label} size="small" sx={{ backgroundColor: cfg.bg, color: cfg.color, fontWeight: 600, fontSize: "11px" }} />;
  };

  return (
    <div style={{ textAlign: "left", padding: "1rem" }}>
      <Toast open={toast.open} handleClose={() => setToast({ ...toast, open: false })}
        severity={toast.severity} message={toast.message} />

      <p style={{ color: "rgba(0,0,0,0.45)", fontWeight: "bold", fontSize: "14px", margin: "0 0 8px" }}>
        Pages / VP Leave Portal
      </p>

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <AdminPanelSettingsIcon sx={{ color: "#244C77", fontSize: "28px" }} />
          <Box>
            <Typography sx={{ fontWeight: "bold", fontSize: "22px", color: "#111827" }}>VP Leave Portal</Typography>
            <Typography sx={{ fontSize: "13px", color: "#6B7280" }}>Review and approve teacher leave requests</Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button startIcon={<FilterListIcon />} size="small" onClick={() => setShowFilter(v => !v)}
            sx={{ border: "1px solid #D5D7DA", borderRadius: "8px", color: "#8F95B2", textTransform: "none", fontSize: "13px" }}>
            Filters
          </Button>
          <Button startIcon={<FileDownloadOutlinedIcon />} size="small" onClick={handleExport}
            sx={{ border: "1px solid #D5D7DA", borderRadius: "8px", color: "#8F95B2", textTransform: "none", fontSize: "13px" }}>
            Export
          </Button>
        </Box>
      </Box>

      {showFilter && (
        <Box sx={{ mb: 2 }}>
          <TextField size="small" fullWidth placeholder="Search by teacher, type, status..."
            value={filterText} onChange={e => setFilterText(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: "#8F95B2", fontSize: "18px" }} /></InputAdornment> }}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
          />
        </Box>
      )}

      <Paper elevation={0} sx={{ borderRadius: "16px", boxShadow: "0 2px 12px rgba(0,0,0,0.07)", overflow: "hidden" }}>
        <TableContainer>
          <Table>
            <TableHead sx={{ backgroundColor: "#1E293B" }}>
              <TableRow>
                {["#", "Teacher Name", "Leave From", "Leave To", "Days", "Type", "Reason", "Status", "Actions"].map(h => (
                  <TableCell key={h} sx={{ color: "#fff", fontWeight: 600, fontSize: "12px", whiteSpace: "nowrap" }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {pageRows.length === 0 ? (
                <TableRow><TableCell colSpan={9} sx={{ textAlign: "center", py: 6, color: "#8F95B2" }}>No leave requests found</TableCell></TableRow>
              ) : pageRows.map((row, idx) => (
                <TableRow key={row.id || idx} sx={{ "&:hover": { backgroundColor: "#f9fafb" } }}>
                  <TableCell sx={{ fontSize: "13px", color: "#6B7280" }}>{(page - 1) * ROWS_PER_PAGE + idx + 1}</TableCell>
                  <TableCell sx={{ fontSize: "13px", fontWeight: 600, color: "#111827" }}>{row.teacherName || "—"}</TableCell>
                  <TableCell sx={{ fontSize: "13px", color: "#374151" }}>{row.leaveFrom ? new Date(row.leaveFrom).toLocaleDateString("en-GB") : "—"}</TableCell>
                  <TableCell sx={{ fontSize: "13px", color: "#374151" }}>{row.leaveTo ? new Date(row.leaveTo).toLocaleDateString("en-GB") : "—"}</TableCell>
                  <TableCell sx={{ fontSize: "13px", color: "#374151" }}>{row.totalDays ?? "—"}</TableCell>
                  <TableCell>
                    <Chip label={row.leaveType || "Leave"} size="small"
                      sx={{ backgroundColor: "#EAF1F8", color: "#244C77", fontSize: "11px" }} />
                  </TableCell>
                  <TableCell sx={{ fontSize: "13px", color: "#374151", maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {row.reason || "—"}
                  </TableCell>
                  <TableCell><StatusChip status={row.status} /></TableCell>
                  <TableCell>
                    {row.status === "pending" && (
                      <Box sx={{ display: "flex", gap: 0.5 }}>
                        <Tooltip title="Approve">
                          <IconButton size="small"
                            onClick={() => setActionDialog({ open: true, row, action: "approved", note: "" })}
                            sx={{ color: "#16a34a", "&:hover": { backgroundColor: "#f0fdf4" } }}>
                            <CheckCircleOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Reject">
                          <IconButton size="small"
                            onClick={() => setActionDialog({ open: true, row, action: "rejected", note: "" })}
                            sx={{ color: "#dc2626", "&:hover": { backgroundColor: "#fef2f2" } }}>
                            <CancelOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {pageCount > 1 && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <Pagination count={pageCount} page={page} onChange={(_, v) => setPage(v)} size="small"
              sx={{ "& .MuiPaginationItem-root.Mui-selected": { backgroundColor: "#244C77", color: "#fff" } }} />
          </Box>
        )}
      </Paper>

      {/* Action Dialog */}
      <Dialog open={actionDialog.open} onClose={() => setActionDialog({ ...actionDialog, open: false })} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: "16px", p: 1 } }}>
        <DialogTitle sx={{ fontWeight: 700, fontSize: "17px" }}>
          {actionDialog.action === "approved" ? "Approve Leave Request" : "Reject Leave Request"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography sx={{ fontSize: "14px", color: "#374151", mb: 1.5 }}>
              Teacher: <strong>{actionDialog.row?.teacherName}</strong>
            </Typography>
            <TextField fullWidth size="small" multiline rows={2}
              placeholder="Add a note (optional)..."
              value={actionDialog.note}
              onChange={e => setActionDialog({ ...actionDialog, note: e.target.value })}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setActionDialog({ ...actionDialog, open: false })} fullWidth
            sx={{ border: "1.5px solid #D1D5DB", borderRadius: "10px", textTransform: "none", color: "#374151" }}>
            Cancel
          </Button>
          <Button onClick={handleApproval} fullWidth variant="contained"
            sx={{
              borderRadius: "10px", textTransform: "none", fontWeight: 600, boxShadow: "none",
              backgroundColor: actionDialog.action === "approved" ? "#16a34a" : "#dc2626"
            }}>
            {actionDialog.action === "approved" ? "Approve" : "Reject"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default VPLeavePortal;
